/**
 * ShopLayout - Layout wrapper for shop portal routes
 *
 * This is the new architecture layout for shop-based routes (multi-tenant).
 * It handles shop membership validation and provides shop context to child routes.
 *
 * Key features:
 * - Uses useShopStatus hook to validate shop access
 * - Handles subscription and role validation in one place
 * - Provides ShopContext to child routes with shop info and permissions
 * - No chicken-and-egg problems with guards
 */

import type { FC } from 'react';
import { createContext, useContext, useMemo } from 'react';
import { Outlet, Navigate, useParams, useLocation } from 'react-router-dom';
import { useAuthContext } from '@/app/providers/AuthProvider';
import { AccessBoundary } from '@/app/routing/access/AccessBoundary';
import type { AccessContext, ActorRole } from '@/app/routing/access/types';
import { useShopStatus } from '@/features/auth/hooks/useShopStatus';
import type { ShopRole, SubscriptionStatus } from '@/shared/api/bootstrap';
import { LoadingScreen } from '@/shared/ui/loading';
import { logger } from '@/shared/utils/logger';
import { paths } from '../../builders/paths';

/**
 * Context value for shop portal
 */
export interface ShopContextValue {
  shopId: string;
  shopSlug: string;
  shopName: string;
  shopLogoUrl: string | null;
  role: ShopRole;
  subscriptionStatus: SubscriptionStatus;
  permissions: string[];
  refetch: () => Promise<unknown>;

  // Permission helpers
  canManageShop: boolean;
  canManageStaff: boolean;
  canManageServices: boolean;
  canManageBilling: boolean;
  canViewAnalytics: boolean;
}

const ShopContext = createContext<ShopContextValue | null>(null);

/**
 * Hook to access shop context
 * Must be used within ShopLayout
 */
export const useShopContext = (): ShopContextValue => {
  const context = useContext(ShopContext);
  if (!context) {
    throw new Error('useShopContext must be used within ShopLayout');
  }
  return context;
};

export const useOptionalShopContext = (): ShopContextValue | null => {
  return useContext(ShopContext);
};

/**
 * Check if subscription status is active
 */
const isActiveSubscription = (status: SubscriptionStatus): boolean => {
  return ['active', 'trialing'].includes(status);
};

/**
 * ShopLayout Component
 */
export const ShopLayout: FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  const { isLoading: authLoading, isAuthenticated, accessToken } = useAuthContext();

  const {
    data: shopStatus,
    isLoading: statusLoading,
    error,
    refetch,
  } = useShopStatus({
    shopSlug: slug || '',
    enabled: isAuthenticated && !authLoading && !!accessToken && !!slug,
    accessToken,
  });

  // Build context value with permission helpers
  const contextValue = useMemo<ShopContextValue | null>(() => {
    if (!shopStatus || !shopStatus.role) return null;

    const permissions = shopStatus.permissions || [];

    return {
      shopId: shopStatus.shop.id,
      shopSlug: shopStatus.shop.slug,
      shopName: shopStatus.shop.name,
      shopLogoUrl: shopStatus.shop.logoUrl,
      role: shopStatus.role,
      subscriptionStatus: shopStatus.subscriptionStatus,
      permissions,
      refetch,

      // Permission helpers
      canManageShop: permissions.includes('manage:shop'),
      canManageStaff: permissions.includes('manage:staff'),
      canManageServices: permissions.includes('manage:services'),
      canManageBilling: permissions.includes('manage:billing'),
      canViewAnalytics: permissions.includes('view:analytics'),
    };
  }, [shopStatus, refetch]);

  // Map ShopRole to ActorRole for access context
  const actorRole: ActorRole = shopStatus?.role === 'OWNER' ? 'shop_owner' : shopStatus?.role === 'MANAGER' ? 'shop_manager' : 'staff_provider';

  // Build access context for AccessBoundary (must be before early returns per rules-of-hooks)
  const accessContext = useMemo<AccessContext>(() => ({
    isAuthenticated: true,
    role: actorRole,
    workspace: 'shop',
    subscriptionStatus: shopStatus?.subscriptionStatus || 'none',
    setupComplete: true,
    shopPermissions: shopStatus?.permissions || [],
    shopSlug: slug,
  }), [actorRole, shopStatus?.subscriptionStatus, shopStatus?.permissions, slug]);

  // Missing slug parameter
  if (!slug) {
    logger.warn('ShopLayout: Missing shop slug parameter');
    return <Navigate to={paths.auth.postLogin()} replace />;
  }

  // Loading state
  if (authLoading || statusLoading) {
    return <LoadingScreen message="Loading shop workspace..." />;
  }

  // Not authenticated - redirect to home
  if (!isAuthenticated) {
    logger.warn('ShopLayout: Not authenticated, redirecting to home');
    return <Navigate to={paths.home()} replace />;
  }

  // API error - could be 404 (shop not found) or 403 (no access)
  if (error) {
    logger.error('ShopLayout: Error fetching shop status', { slug, error });
    // Redirect to post-login to re-route
    return <Navigate to={paths.auth.postLogin()} replace />;
  }

  // Shop not found or no access (null role means no membership)
  if (!shopStatus || !shopStatus.role) {
    logger.warn('ShopLayout: No shop access', {
      slug,
      hasShopStatus: !!shopStatus,
    });
    // Redirect to post-login to re-route
    return <Navigate to={paths.auth.postLogin()} replace />;
  }

  const { subscriptionStatus } = shopStatus;

  // No active subscription - handle based on role
  if (!isActiveSubscription(subscriptionStatus)) {
    if (shopStatus.role === 'OWNER') {
      // Owner can access billing to fix subscription
      if (!location.pathname.includes('/billing')) {
        logger.info(
          'ShopLayout: Owner with inactive subscription, redirecting to billing',
          {
            slug,
            currentStatus: subscriptionStatus,
          },
        );
        return <Navigate to={`/shops/${slug}/billing`} replace />;
      }
    } else {
      // Staff/Renter - show limited access (redirect to post-login)
      logger.info('ShopLayout: Staff/Renter with inactive shop subscription', {
        slug,
        role: shopStatus.role,
      });
      return <Navigate to={paths.auth.postLogin()} replace />;
    }
  }

  // All checks passed - render children with context
  return (
    <ShopContext.Provider value={contextValue}>
      <AccessBoundary context={accessContext}>
        <Outlet />
      </AccessBoundary>
    </ShopContext.Provider>
  );
};
